import React, { useState, useCallback, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import DirectionStep from './DirectionStep';
import RouteSummary from './RouteSummary';
import { parseRouteSteps, isValidRouteData } from '../../utils/routeParser';

/**
 * RouteDetailsPanel - Full-height side panel showing step-by-step route directions.
 * Renders via a Portal so it's not clipped by the parent RoutingPanel's overflow.
 * Opens with slide animation when user clicks "Details" on a route card.
 * 
 * In multi-stop routes, shows intermediate destination markers (black dot + add icon)
 * between legs and a final destination marker (where-to-vote icon) at the end.
 */
const RouteDetailsPanel = ({ routeData, isOpen, onClose, isMobile, stops = [], onStepHover, onStepLeave, onStepClick }) => {
  const [activeStep, setActiveStep] = useState(null);
  const stepsContainerRef = useRef(null);
  const panelRef = useRef(null);

  // Parse maneuvers into renderable steps, passing stops for multi-leg waypoint markers
  const legs = routeData?.legs || [];
  const steps = parseRouteSteps(legs, stops);
  const hasValidData = isValidRouteData(routeData);

  // Get source/destination display names and addresses
  const sourceName = routeData?.pointADetails?.entity_name ||
    routeData?.pointADetails?.display_name ||
    routeData?.pointADetails?.name || '';
  const sourceAddress = routeData?.pointADetails?.entity_address ||
    routeData?.pointADetails?.display_address ||
    routeData?.pointADetails?.display_text ||
    routeData?.pointADetails?.full_address ||
    routeData?.pointADetails?.clean_address ||
    routeData?.pointADetails?.address || '';
  const destinationName = routeData?.pointBDetails?.entity_name ||
    routeData?.pointBDetails?.display_name ||
    routeData?.pointBDetails?.name || '';
  const rawDestAddress = routeData?.pointBDetails?.entity_address ||
    routeData?.pointBDetails?.display_text ||
    routeData?.pointBDetails?.full_address ||
    routeData?.pointBDetails?.clean_address ||
    routeData?.pointBDetails?.address || '';
  // Remove entity_name from the beginning of address to avoid duplication
  let destinationAddress = rawDestAddress;
  if (destinationName && rawDestAddress.startsWith(destinationName)) {
    destinationAddress = rawDestAddress.slice(destinationName.length).replace(/^[,\s]+/, '').trim();
  }

  const handleStepHover = useCallback((index) => {
    setActiveStep(index);
    if (onStepHover) {
      // Compute the dot index: count how many non-arrive/non-waypoint steps
      // appear before and including this index (dots show depart + regular maneuvers)
      let dotIndex = -1;
      for (let i = 0; i <= index && i < steps.length; i++) {
        const s = steps[i];
        if (!s.isArrive && !s.isWaypoint && !s.isFinalDestination) {
          dotIndex++;
        }
      }
      if (dotIndex >= 0) {
        onStepHover(dotIndex);
      }
    }
  }, [onStepHover, steps]);

  const handleStepLeave = useCallback(() => {
    setActiveStep(null);
    if (onStepLeave) onStepLeave();
  }, [onStepLeave]);

  const handleStepClick = useCallback((index) => {
    if (onStepClick) {
      // Same dot-index mapping as hover
      let dotIndex = -1;
      for (let i = 0; i <= index && i < steps.length; i++) {
        const s = steps[i];
        if (!s.isArrive && !s.isWaypoint && !s.isFinalDestination) {
          dotIndex++;
        }
      }
      if (dotIndex >= 0) {
        onStepClick(dotIndex);
      }
    }
  }, [onStepClick, steps]);

  // Handle Escape key to close
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  // Reset scroll position when panel opens
  useEffect(() => {
    if (isOpen && stepsContainerRef.current) {
      stepsContainerRef.current.scrollTop = 0;
    }
    setActiveStep(null);
  }, [isOpen, routeData]);

  if (!isOpen) return null;

  // For multi-stop routes, update the last arrive step with destination details
  if (steps.length > 0) {
    const lastStep = steps[steps.length - 1];
    if (lastStep.isFinalDestination || lastStep.isArrive) {
      if (destinationName) lastStep.instruction = destinationName;
      if (destinationAddress) lastStep.address = destinationAddress;
      lastStep.isFinalDestination = true;
      lastStep.icon = '/icons/where-to-vote.svg';
    }
  }

  const panel = (
    <div
      ref={panelRef}
      className={`route-directions-panel ${isMobile ? 'route-directions-panel-mobile' : ''}`}
      role="dialog"
      aria-label="Route Details"
      aria-modal="false"
    >
      {/* Header */}
      <div className="route-directions-header">
        <button
          className="route-directions-back-btn"
          onClick={onClose}
          aria-label="Back to routes"
        >
          <img src="/icons/arrow-back.svg" alt="Back" style={{ width: '24px', height: '24px' }} />
        </button>
        <h3 className="route-directions-title">Route Details</h3>
        <button
          className="route-directions-close-btn"
          onClick={onClose}
          aria-label="Close route details"
        >
          <img src="/icons/Close.svg" alt="Close" style={{ width: '24px', height: '24px' }} />
        </button>
      </div>

      {/* Scrollable body */}
      <div ref={stepsContainerRef} className="route-directions-body">
        {/* Summary */}
        {routeData && (
          <RouteSummary
            distance={routeData.distance}
            duration={routeData.duration}
            summary={routeData.summary}
            sourceName={sourceName}
            sourceAddress={sourceAddress}
            destinationName={destinationName}
            destinationAddress={destinationAddress}
          />
        )}

        {/* Steps List */}
        <div
          className="route-directions-steps"
          role="list"
          aria-label="Turn-by-turn directions"
        >
          {hasValidData && steps.length > 0 ? (
            steps.map((step, index) => (
              <DirectionStep
                key={index}
                step={step}
                index={index}
                isActive={activeStep === index}
                isLast={index === steps.length - 1}
                onHover={handleStepHover}
                onLeave={handleStepLeave}
                onClick={handleStepClick}
              />
            ))
          ) : (
            <div className="route-directions-empty">
              <p>No turn-by-turn directions available for this route.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  // Render as Portal to avoid clipping by parent overflow
  return createPortal(panel, document.body);
};

export default RouteDetailsPanel;
